import React, { useEffect, useState } from 'react';
import { TokenManager } from '../../utils/tokenManager';
import {
  AppBar,
  Box,
  Toolbar as MuiToolbar,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import Logout from '../../assets/icons/logout.svg';
import NewTicket from '../../pages/ticket/NewTicket';
// import { ReactComponent as MenuBookIcon } from '../../assets/icons/menubook.svg';
// import { ReactComponent as NotifyIcon } from '../../assets/icons/notification.svg';
// import { ReactComponent as HelpIcon } from '../../assets/icons/help.svg';
import { ReactComponent as LogoIcon } from '../../assets/icons/utflogo.svg';
import { ReactComponent as Logo } from '../../assets/icons/logog.svg';
import { Button } from '@ui-components/ui-library';
import { useTranslation } from '../../hooks/useTranslation';

interface ToolbarProps {
  handleDrawerToggle: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({ handleDrawerToggle }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLaptop = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const msal = useMsal();
  const [userName, setUserName] = useState<string>(msal.accounts[0]?.username || '');

  const imgFromSession = sessionStorage.getItem('imgUrl');
  const [imageUrl, setImageUrl] = useState(imgFromSession ? imgFromSession : '');
  const accessToken = sessionStorage.getItem('accessTokenP');

  const handleLogout = () => {
    try {
      TokenManager.clearAll();
      msal.instance.logoutRedirect();
    } catch (e) {
      throw new Error('tokan error: ' + e);
    }
  };

  useEffect(() => {
    const getBlobData = async () => {
      try {
        const response = await axios.get('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: { Authorization: accessToken },
          responseType: 'blob',
        });

        const url = window.URL || window.webkitURL;
        const blobUrl = url.createObjectURL(response.data);
        TokenManager.setImageUrl(blobUrl);
        setImageUrl(blobUrl);
      } catch (error: unknown) {
        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'data' in error.response &&
          error.response.data &&
          typeof error.response.data === 'object' &&
          'error' in error.response.data &&
          error.response.data.error &&
          typeof error.response.data.error === 'object' &&
          'code' in error.response.data.error &&
          error.response.data.error.code === 'InvalidAuthenticationToken'
        ) {
          try {
            await TokenManager.refreshTokenWithRetry(
              msal.instance,
              msal.accounts[0],
              'frontend',
              undefined,
              async () => {
                const photoResponse = await axios.get(
                  'https://graph.microsoft.com/v1.0/me/photo/$value',
                  {
                    headers: { Authorization: TokenManager.getFrontendToken() },
                    responseType: 'blob',
                  }
                );

                const url = window.URL || window.webkitURL;
                const blobUrl = url.createObjectURL(photoResponse.data);
                TokenManager.setImageUrl(blobUrl);
                setImageUrl(blobUrl);

                return blobUrl;
              }
            );
          } catch (tokenError) {
            // Handle token refresh failure silently or show user-friendly error
            // Could be logged to an error tracking service in production
          }
        }
      }
    };

    getBlobData();
    if (msal.accounts != null && msal.accounts?.length > 0) {
      setUserName(msal.accounts[0].name ? msal.accounts[0].name : '');
    }
  }, [accessToken, msal.accounts, msal.instance]);

  const [openNewTicket, setOpenNewTicket] = useState(false);

  const handleOpen = () => {
    setOpenNewTicket(true);
  };

  const handleClose = () => {
    setOpenNewTicket(false);
  };

  return (
    <AppBar
      position="fixed"
      color="default"
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        boxShadow: 'none',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e0e0e0',
        minHeight: isMobile ? '56px' : '64px',
      }}
    >
      <MuiToolbar
        sx={{
          minHeight: isMobile ? '56px' : '64px',
          padding: isMobile ? '0 8px' : '0 16px',
        }}
      >
        {(isMobile || isTablet) && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: isMobile ? 1 : 2,
              ml: 0.5,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: isMobile ? 0.5 : isTablet ? 1.5 : 3,
            mr: isMobile ? 1 : isTablet ? 1.5 : 2,
          }}
        >
          <Box
            component={Logo}
            sx={{
              width: isMobile ? '100px' : isTablet ? '110px' : '120px',
              height: isMobile ? '45px' : isTablet ? '49px' : '54px',
              marginRight: isMobile ? 1 : isTablet ? 1.5 : 2,
            }}
          />
          {isLaptop && (
            <Divider
              orientation="vertical"
              variant="fullWidth"
              flexItem
              sx={{ height: '72px', mr: 2, ml: 1 }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: isMobile ? 28 : isTablet ? 30 : 32,
                height: isMobile ? 28 : isTablet ? 30 : 32,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: isMobile ? 0.5 : isTablet ? 0.75 : 1,
              }}
            >
              <Box
                component={LogoIcon}
                sx={{
                  width: isMobile ? '28px' : isTablet ? '30px' : '32px',
                  height: isMobile ? '28px' : isTablet ? '30px' : '32px',
                }}
              />
            </Box>
            {!isMobile && (
              <Typography
                sx={{
                  color: '#054E5A',
                  fontWeight: '700',
                  fontFamily: "'Source Sans Pro', Helvetica",
                  fontSize: isTablet ? '1.1rem' : '1.25rem',
                }}
              >
                {t('toolbar.upgrades_tool')}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 1 : isTablet ? 1.5 : 2,
            mr: isMobile ? 0.5 : isTablet ? 0.75 : 1,
          }}
        >
          <>
            {!isMobile && (
              <Button
                onClick={handleOpen}
                variant="solid"
                color="primary"
                startIcon={<AddCircleOutlineOutlinedIcon />}
                sx={{
                  fontSize: '14px',
                  px: isTablet ? 1.5 : 2,
                  py: isTablet ? 0.75 : 1,
                }}
              >
                {t('toolbar.new_ticket')}
              </Button>
            )}
            {isMobile && (
              <Button
                onClick={handleOpen}
                color="primary"
                variant="solid"
                sx={{
                  minWidth: '40px',
                  padding: '8px',
                  width: '40px',
                  height: '40px',
                }}
              >
                <AddCircleOutlineOutlinedIcon />
              </Button>
            )}
            <NewTicket open={openNewTicket} handleClose={handleClose} />
          </>
          {/* <Box
            sx={{
              display: 'flex',
              gap: isMobile ? 0.5 : isTablet ? 0.75 : 1,
              mr: isMobile ? 0.25 : isTablet ? 0.5 : 0.75,
            }}
          >
            {isLaptop && (
              <IconButton
                color="primary"
                size="medium"
                sx={{
                  p: 1,
                }}
              >
                <MenuBookIcon />
              </IconButton>
            )}
            {isLaptop && (
              <IconButton
                color="primary"
                size="medium"
                sx={{
                  p: 1,
                }}
              >
                <HelpIcon />
              </IconButton>
            )}
            {isLaptop && (
              <IconButton
                color="primary"
                sx={{
                  position: 'relative',
                  p: 1,
                }}
                size="medium"
              >
                <Badge
                  badgeContent={4}
                  color="warning"
                  sx={{ position: 'fixed', l: '0px', height: '20px', width: '20px' }}
                />
                <NotifyIcon />
              </IconButton>
            )}
          </Box> */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: isMobile ? '6px 4px' : isTablet ? '7px 6px' : '8px 8px',
              borderRadius: '4px',
              bgcolor: '#ffffff',
              mr: isMobile ? 0.25 : isTablet ? 0.5 : 0.75,
            }}
          >
            <Avatar
              src={imageUrl}
              alt={userName}
              sx={{
                width: isMobile ? 36 : isTablet ? 38 : 40,
                height: isMobile ? 36 : isTablet ? 38 : 40,
                marginRight: isLargeScreen ? '10px' : '0px',
              }}
            />
            {isLargeScreen && (
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{
                  lineHeight: 1.2,
                  color: '#032B32',
                  fontSize: isTablet ? '0.875rem' : '1rem',
                }}
              >
                {userName}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={handleLogout}
            aria-label="Logout"
            title="Logout"
            sx={{
              p: 1,
              mr: isMobile ? 0.25 : isTablet ? 0.5 : 0.75,
            }}
          >
            <img src={Logout} alt="" />
          </IconButton>
        </Box>
      </MuiToolbar>
    </AppBar>
  );
};

export default Toolbar;
